import type { CampaignObjective } from '../types/metaAdDraft.js';

// Stage 7A D2.17 — pure helpers for the campaign objective / ad-set optimization
// goal / custom event type model. Shared by the compose UI, the publish mapper,
// the validator, and the legacy-draft migration. No React, no I/O.

export type DestinationType = 'instant_form' | 'website';

// Optimization-goal option sets per objective + destination.
export const TRAFFIC_OPTIMIZATION_GOALS = ['LINK_CLICKS', 'LANDING_PAGE_VIEWS', 'REACH', 'IMPRESSIONS'] as const;
export const LEADS_INSTANT_FORM_OPTIMIZATION_GOAL = 'LEAD_GENERATION' as const;
export const LEADS_WEBSITE_OPTIMIZATION_GOALS = ['OFFSITE_CONVERSIONS', 'LANDING_PAGE_VIEWS', 'LINK_CLICKS'] as const;

// D2.20 — Meta pixel events for OFFSITE_CONVERSIONS, split by the Meta objective
// they belong to (Miki 2026-06-05). Lead-class events route to OUTCOME_LEADS (via
// Zernio goal 'lead_conversion'); sales-class events route to OUTCOME_SALES (goal
// 'conversions'). The mapper auto-routes based on the chosen event.
export const LEADS_CLASS_EVENTS = ['LEAD', 'SUBMIT_APPLICATION', 'SCHEDULE', 'CONTACT'] as const;
export const SALES_CLASS_EVENTS = [
  'PURCHASE',
  'ADD_TO_CART',
  'START_TRIAL',
  'COMPLETE_REGISTRATION',
  'VIEW_CONTENT',
  'SUBSCRIBE',
  'INITIATE_CHECKOUT',
] as const;

// The full set offered for OFFSITE_CONVERSIONS (both classes; validation accepts any).
export const CUSTOM_EVENT_TYPES = [...LEADS_CLASS_EVENTS, ...SALES_CLASS_EVENTS] as const;

// D2.20 — which Zernio goal an OFFSITE_CONVERSIONS event routes to. Lead-class →
// 'lead_conversion' (OUTCOME_LEADS); everything else → 'conversions' (OUTCOME_SALES).
export function getGoalForEvent(event: string): 'lead_conversion' | 'conversions' {
  return (LEADS_CLASS_EVENTS as readonly string[]).includes(event) ? 'lead_conversion' : 'conversions';
}

// Human labels for the optimization-goal dropdown.
export const OPTIMIZATION_GOAL_LABELS: Record<string, string> = {
  LINK_CLICKS: 'Link clicks',
  LANDING_PAGE_VIEWS: 'Landing page views',
  REACH: 'Reach',
  IMPRESSIONS: 'Impressions',
  LEAD_GENERATION: 'Lead form submissions',
  OFFSITE_CONVERSIONS: 'Conversions (pixel event)',
};

export const CUSTOM_EVENT_TYPE_LABELS: Record<string, string> = {
  LEAD: 'Lead',
  SUBMIT_APPLICATION: 'Submit application',
  SCHEDULE: 'Schedule',
  CONTACT: 'Contact',
  PURCHASE: 'Purchase',
  ADD_TO_CART: 'Add to cart',
  START_TRIAL: 'Start trial',
  COMPLETE_REGISTRATION: 'Complete registration',
  VIEW_CONTENT: 'View content',
  SUBSCRIBE: 'Subscribe',
  INITIATE_CHECKOUT: 'Initiate checkout',
};

// The valid optimization goals for a given objective + destination.
export function validOptimizationGoals(
  objective: CampaignObjective,
  destinationType: DestinationType,
): string[] {
  if (objective === 'LEADS') {
    return destinationType === 'instant_form'
      ? [LEADS_INSTANT_FORM_OPTIMIZATION_GOAL]
      : [...LEADS_WEBSITE_OPTIMIZATION_GOALS];
  }
  return [...TRAFFIC_OPTIMIZATION_GOALS];
}

// The default optimization goal for a fresh objective+destination combination
// (used on new drafts + when the objective/destination changes and the current
// value is no longer valid). leads+website defaults to LANDING_PAGE_VIEWS (no
// pixel required) — OFFSITE_CONVERSIONS is the AM's deliberate, recommended pick.
export function defaultOptimizationGoal(
  objective: CampaignObjective,
  destinationType: DestinationType,
): string {
  if (objective === 'LEADS') {
    return destinationType === 'instant_form' ? 'LEAD_GENERATION' : 'LANDING_PAGE_VIEWS';
  }
  return 'LINK_CLICKS';
}

function asDestination(v: unknown): DestinationType {
  return v === 'website' ? 'website' : 'instant_form';
}

// Migrate a legacy/partial (campaign, adSet) to the D2.17 objective model.
// Idempotent: a valid objective + optimizationGoal are kept. Legacy drafts carry
// `campaign.goal` ('leads' | 'traffic') and no optimizationGoal/customEventType.
//   goal='traffic'                         → TRAFFIC + LINK_CLICKS
//   goal='leads' + destination=instant_form → LEADS  + LEAD_GENERATION
//   goal='leads' + destination=website      → LEADS  + LANDING_PAGE_VIEWS
// customEventType is kept only for OFFSITE_CONVERSIONS, else null.
export function migrateObjectiveShape(
  campaign: { objective?: unknown; goal?: unknown },
  adSet: { destinationType?: unknown; optimizationGoal?: unknown; customEventType?: unknown },
): { objective: CampaignObjective; optimizationGoal: string; customEventType: string | null } {
  const destinationType = asDestination(adSet?.destinationType);

  const objective: CampaignObjective =
    campaign?.objective === 'TRAFFIC' || campaign?.objective === 'LEADS'
      ? campaign.objective
      : campaign?.goal === 'traffic'
        ? 'TRAFFIC'
        : 'LEADS'; // legacy 'leads' (or missing) → LEADS

  const valid = validOptimizationGoals(objective, destinationType);
  const optimizationGoal =
    typeof adSet?.optimizationGoal === 'string' && valid.includes(adSet.optimizationGoal)
      ? adSet.optimizationGoal
      : defaultOptimizationGoal(objective, destinationType);

  const customEventType =
    optimizationGoal === 'OFFSITE_CONVERSIONS' && typeof adSet?.customEventType === 'string' && adSet.customEventType !== ''
      ? adSet.customEventType
      : null;

  return { objective, optimizationGoal, customEventType };
}
